import { useContext, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { LanguageContext } from "@/i18n";

const EASE = [0.16, 1, 0.3, 1];

export default function Hero() {
  const { lang, t } = useContext(LanguageContext);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yContent = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const yGrid = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const go = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72, duration: 1.4 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* animated dark-tech grid */}
      <motion.div style={{ y: yGrid }} className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 grid-bg opacity-100" />
        <motion.div
          animate={{ y: [0, -36, 0], x: [0, 24, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-[18%] start-[8%] w-[420px] h-[420px] rounded-full bg-[#00F0FF]/[0.07] blur-[120px]"
        />
        <motion.div
          animate={{ y: [0, 40, 0], x: [0, -30, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-[10%] end-[6%] w-[520px] h-[520px] rounded-full bg-[#00F0FF]/[0.05] blur-[140px]"
        />
        <motion.div
          animate={{ opacity: [0.05, 0.14, 0.05] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1758073519996-6d3c63b4922c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjB0ZWNobm9sb2d5JTIwbmV0d29ya3xlbnwwfHx8fDE3ODYxNzc5MTJ8MA&ixlib=rb-4.1.0&q=85')",
          }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/40 via-transparent to-[#0A0A0A] pointer-events-none" />

      <motion.div style={{ y: yContent, opacity: fade }} className="relative z-10 max-w-screen-xl mx-auto px-6 md:px-12 w-full pt-32 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
          className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full border border-white/10 bg-white/[0.03] mb-10"
        >
          <span className="relative flex w-2 h-2">
            <span className="absolute inline-flex w-full h-full rounded-full bg-[#00F0FF] opacity-60 animate-ping" />
            <span className="relative inline-flex w-2 h-2 rounded-full bg-[#00F0FF]" />
          </span>
          <span data-testid="hero-badge" className="font-mono text-[11px] tracking-[0.2em] uppercase text-zinc-400">{t.hero.badge}</span>
        </motion.div>

        <h1 data-testid="hero-title" className="font-display font-black leading-[0.94] tracking-tight text-white text-[13.5vw] sm:text-7xl lg:text-[6.5rem]">
          {t.hero.lines.map((line, i) => (
            <span key={`${lang}-${i}`} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.35 + i * 0.14, ease: EASE }}
                className={`block ${i === 0 ? "" : "text-outline"}`}
              >
                {line}
                {i === 0 && <span className="text-[#00F0FF]">.</span>}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-10">
          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
            className="max-w-xl text-base md:text-lg font-light text-zinc-400 leading-relaxed"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
            className="flex items-center gap-4 shrink-0"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => go("services")}
              className="group flex items-center gap-2 px-7 py-4 rounded-full bg-[#00F0FF] text-black text-sm font-semibold hover:shadow-[0_0_45px_rgba(0,240,255,0.4)] transition-shadow duration-300"
            >
              {t.hero.ctaPrimary}
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 rtl:-scale-x-100" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => go("contact")}
              className="px-7 py-4 rounded-full border border-white/15 text-sm font-medium text-zinc-200 hover:border-[#00F0FF]/60 hover:text-white transition-colors duration-300"
            >
              {t.hero.ctaSecondary}
            </button>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-zinc-600"
      >
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase">{t.hero.scroll}</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
          <ArrowDown size={14} />
        </motion.span>
      </motion.div>
    </section>
  );
}
